import { useState, useEffect } from "react";
import {
  Stack,
  Group,
  Text,
  Switch,
  Paper,
  Badge,
  SegmentedControl,
} from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import { jsonGet, jsonPost, jsonDelete, jsonPatch } from "../api";

export default function WeeklyReportSettings({ brand }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [frequency, setFrequency] = useState("Weekly");

  useEffect(() => {
    if (brand?.id) {
      loadSchedule();
    }
  }, [brand?.id]);

  const loadSchedule = async () => {
    try {
      const schedules = await jsonGet(
        `weekly-report-schedules?brandId=${brand.id}`
      );
      if (schedules?.length > 0) {
        setSchedule(schedules[0]);
        // Backend may return lowercase enum (e.g. "weekly") — capitalize for SegmentedControl
        const freq = schedules[0].frequency ?? "Weekly";
        setFrequency(freq.charAt(0).toUpperCase() + freq.slice(1));
      }
    } catch (err) {
      console.error("Failed to load weekly report schedule:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    try {
      if (enabled) {
        const newSchedule = await jsonPost("weekly-report-schedules", {
          brandId: brand.id,
          frequency,
        });
        setSchedule(newSchedule);
      } else if (schedule) {
        await jsonDelete(`weekly-report-schedules/${schedule.id}`);
        setSchedule(null);
      }
    } catch (err) {
      console.error("Failed to toggle weekly report:", err);
    }
  };

  const handleFrequencyChange = async (newFrequency) => {
    setFrequency(newFrequency);
    if (schedule) {
      try {
        const updated = await jsonPatch(
          `weekly-report-schedules/${schedule.id}`,
          { frequency: newFrequency }
        );
        setSchedule(updated);
      } catch (err) {
        console.error("Failed to update frequency:", err);
      }
    }
  };

  if (loading) return null;


  return (
    <Paper mt="md" p="md" withBorder>
      <Stack gap="md">
        <Group>
          <IconMail size={20} />
          <Text fw={600}>Email Digest</Text>
        </Group>

        <Text size="sm" c="dimmed">
          One email tying together web traffic, Search Console, AI visibility probes
          and new brand mentions — with a written summary of what changed and what to
          do about it.
        </Text>

        <Group justify="space-between">
          <Switch
            label="Enable email digest"
            checked={!!schedule}
            onChange={(e) => handleToggle(e.currentTarget.checked)}
          />
          {schedule && (
            <Group gap="xs">
              <Badge variant="light" size="sm">
                {frequency === "Daily" ? "Daily at 8 AM UTC" : "Weekly at 8 AM UTC"}
              </Badge>
              <Badge variant="light" color="blue" size="sm">
                Next: {new Date(schedule.nextRun).toLocaleDateString()}
              </Badge>
            </Group>
          )}
        </Group>

        {schedule && (
          <SegmentedControl
            value={frequency}
            onChange={handleFrequencyChange}
            data={["Daily", "Weekly"]}
          />
        )}
      </Stack>
    </Paper>
  );
}
